import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Project, Column, Task, Note, Attachment, SyncQueueItem } from '../types';
import { offlineDb } from './offlineDb';

const hasOwn = (object: object, property: PropertyKey): boolean => Object.prototype.hasOwnProperty.call(object, property);

export async function apiCall(url: string, options: RequestInit = {}) {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const token = localStorage.getItem('kan-do-token');
  const res = await fetch(`${apiUrl}${url}`, {
    ...options,
    headers: {
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('kan-do-token');
    window.location.reload();
    throw new Error('Session expired');
  }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res;
}

function parseQueueEntry(url: string, method: string): {
  entityType: SyncQueueItem['entityType'];
  entityId: string;
  op: SyncQueueItem['operation'];
} {
  const m = (method || 'POST').toUpperCase();
  const op: SyncQueueItem['operation'] = m === 'POST' ? 'create' : m === 'DELETE' ? 'delete' : 'update';
  const taskMatch = url.match(/^\/api\/tasks\/?([^/?]*)/);
  if (taskMatch) return { entityType: 'task', entityId: taskMatch[1] ?? '', op };
  const colMatch = url.match(/^\/api\/columns\/?([^/?]*)/);
  if (colMatch) return { entityType: 'column', entityId: colMatch[1] ?? '', op };
  const projMatch = url.match(/^\/api\/projects\/?([^/?]*)/);
  if (projMatch) return { entityType: 'project', entityId: projMatch[1] ?? '', op };
  return { entityType: 'task', entityId: '', op };
}

function syncApiCall(url: string, options: RequestInit, label: string, boardId?: string) {
  void (async () => {
    if (!navigator.onLine && boardId) {
      const method = (options.method ?? 'POST').toUpperCase();
      const { entityType, entityId, op } = parseQueueEntry(url, method);
      try {
        await offlineDb.syncQueue.add({
          operation: op,
          entityType,
          entityId,
          boardId,
          data: options.body instanceof FormData
            ? {}
            : options.body
              ? (JSON.parse(options.body as string) as Record<string, unknown>)
              : {},
          baseVersion: null,
          queuedAt: new Date().toISOString(),
          status: 'pending',
          retries: 0,
        });
      } catch (qErr) {
        console.warn(`Failed to queue ${label}:`, qErr);
      }
      return;
    }
    try {
      await apiCall(url, options);
    } catch (err) {
      console.warn(`Failed to sync ${label}:`, err);
    }
  })();
}

function projectApiFields(project: Partial<Project>) {
  return {
    ...(project.id !== undefined ? { id: project.id } : {}),
    ...(project.name !== undefined ? { name: project.name } : {}),
    ...(project.color !== undefined ? { color: project.color } : {}),
    ...(project.category !== undefined ? { category: project.category } : {}),
    ...(project.archived !== undefined ? { archived: project.archived } : {}),
    ...(project.tags !== undefined ? { tags: project.tags } : {}),
  };
}

function columnApiFields(column: Partial<Column>) {
  return {
    ...(column.projectId !== undefined ? { project_id: column.projectId } : {}),
    ...(column.title !== undefined ? { title: column.title } : {}),
    ...(column.color !== undefined ? { color: column.color } : {}),
    ...(column.wipLimit !== undefined ? { wip_limit: column.wipLimit } : {}),
  };
}

function taskApiFields(task: Partial<Task>) {
  return {
    ...(task.columnId !== undefined ? { column_id: task.columnId } : {}),
    ...(task.title !== undefined ? { title: task.title } : {}),
    ...(task.notes !== undefined ? { notes: task.notes } : {}),
    ...(hasOwn(task, 'dueDate') ? { due_date: task.dueDate ?? null } : {}),
    ...(hasOwn(task, 'priority') ? { priority: task.priority ?? null } : {}),
    ...(task.tags !== undefined ? { tags: task.tags } : {}),
    ...(hasOwn(task, 'assignee') ? { assignee: task.assignee ?? null } : {}),
    ...(task.subtasks !== undefined ? { subtasks: task.subtasks } : {}),
    ...(task.cost !== undefined ? { cost: task.cost } : {}),
    ...(task.time !== undefined ? { time: task.time } : {}),
    ...(task.comments !== undefined ? { comments: task.comments } : {}),
    ...(task.attachments !== undefined ? { attachments: task.attachments } : {}),
  };
}

export interface UserProfile {
  id: string;
  username: string;
  isAdmin: boolean;
}

export interface ProjectPermissions {
  canEditTasks: boolean;
  canEditSubtasks: boolean;
  canComment: boolean;
}

export interface ProjectShare {
  userId: string;
  username: string;
  permissions: ProjectPermissions;
}

export interface ActivityEntry {
  id: string;
  userId: string;
  userName: string;
  projectId: string;
  action: string;
  entityType?: string;
  entityId?: string;
  description: string;
  createdAt: string;
}

interface KanbanStore {
  activeProjectId: string;
  projects: Project[];
  columns: Column[];
  tasks: Task[];
  trashedTasks: Task[];
  lastDeletedTask: Task | null;

  // User Management State
  currentUser: UserProfile | null;
  users: UserProfile[];
  projectShares: Record<string, ProjectShare[]>;

  // View Settings
  showTime: boolean;
  showCost: boolean;
  showRawHours: boolean;
  darkMode: boolean;
  filterHasAttachments: boolean;
  searchQuery: string;

  // Actions
  setActiveProject: (id: string) => void;
  moveTask: (taskId: string, targetColumnId: string, newIndex: number) => void;
  moveColumn: (columnId: string, newIndex: number) => void; // <-- ADDED
  toggleSubtask: (taskId: string, subtaskId: string) => void;

  // User Actions
  setCurrentUser: (user: UserProfile | null) => void;
  fetchUsers: () => Promise<void>;
  createUser: (username: string, password: string, isAdmin: boolean) => Promise<void>;
  updateUserAdminStatus: (userId: string, isAdmin: boolean) => Promise<void>;
  resetPassword: (userId: string, newPassword: string) => Promise<void>;

  // Sharing and activity
  userPermissions: (projectId: string) => ProjectPermissions & { isOwner: boolean };
  fetchShares: (projectId: string) => Promise<void>;
  shareProject: (projectId: string, userId: string, permissions: ProjectPermissions) => Promise<void>;
  unshareProject: (projectId: string, userId: string) => Promise<void>;
  searchUsers: (query: string) => Promise<UserProfile[]>;
  fetchActivity: (projectId?: string) => Promise<ActivityEntry[]>;

  // View Settings Actions
  toggleShowTime: () => void;
  toggleShowCost: () => void;
  toggleShowHours: () => void;
  toggleDarkMode: () => void;
  setFilterHasAttachments: (val: boolean) => void;
  setSearchQuery: (query: string) => void;

  // Project & Column Actions
  addProject: (project: Omit<Project, 'id'>) => void;
  updateProject: (id: string, project: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  addColumn: (column: Omit<Column, 'id'>) => void;
  updateColumn: (id: string, column: Partial<Column>) => void;
  deleteColumn: (id: string) => void;

  // Task Actions
  addTask: (task: Omit<Task, 'id'>) => void;
  updateTask: (taskId: string, updatedFields: Partial<Task>) => void;
  deleteTask: (taskId: string) => void;
  restoreTask: (taskId?: string) => void;
  clearLastDeletedTask: () => void;
  purgeTrash: () => void;

  // Comments, Notes, and Files Actions
  addComment: (taskId: string, text: string, author?: string) => void;
  deleteComment: (taskId: string, commentId: string) => void;
  addAttachment: (taskId: string, attachment: Omit<Attachment, 'id' | 'createdAt'>) => void;
  deleteAttachment: (taskId: string, attachmentId: string) => void;
  addSubtaskNote: (taskId: string, subtaskId: string, text: string, author?: string) => void;
  deleteSubtaskNote: (taskId: string, subtaskId: string, noteId: string) => void;

  // Backup / Import & Async Actions
  importStoreData: (data: { projects: Project[]; columns: Column[]; tasks: Task[] }) => void;
  fetchInitialData: () => Promise<void>;

  // Selectors
  getActiveProjectColumns: () => Column[];
  getColumnTasks: (columnId: string) => Task[];
}

export const useKanbanStore = create<KanbanStore>()(
  persist(
    (set, get) => ({
      activeProjectId: '',
      trashedTasks: [],
      lastDeletedTask: null,

      currentUser: null,
      users: [],
      projectShares: {},

      showTime: true,
      showCost: true,
      showRawHours: false,
      darkMode: false,
      filterHasAttachments: false,
      searchQuery: '',

      projects: [],
      columns: [],
      tasks: [],

      setActiveProject: (id) => set({ activeProjectId: id }),
      setCurrentUser: (user) => set({ currentUser: user }),
      toggleShowTime: () => set((state) => ({ showTime: !state.showTime })),
      toggleShowCost: () => set((state) => ({ showCost: !state.showCost })),
      toggleShowHours: () => set((state) => ({ showRawHours: !state.showRawHours })),
      setFilterHasAttachments: (val) => set({ filterHasAttachments: val }),
      setSearchQuery: (query) => set({ searchQuery: query }),
      
      toggleDarkMode: () => {
        const newDarkMode = !get().darkMode;
        if (newDarkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        set({ darkMode: newDarkMode });
      },

      fetchInitialData: async () => {
        try {
          const response = await apiCall('/api/state');
          const data = await response.json();
          const projects = Array.isArray(data.projects) ? data.projects : [];
          set({
            projects,
            columns: Array.isArray(data.columns) ? data.columns : [],
            tasks: Array.isArray(data.tasks) ? data.tasks : [],
            projectShares: data.shares && typeof data.shares === 'object' ? data.shares : {},
            activeProjectId: projects[0]?.id || '',
          });
          await get().fetchUsers();
        } catch (err) {
          console.error('Failed to fetch from backend:', err);
        }
      },

      fetchUsers: async () => {
        const res = await apiCall('/api/users');
        const users = await res.json();
        set({ users: Array.isArray(users) ? users : [] });
      },

      createUser: async (username, password, isAdmin) => {
        const res = await apiCall('/api/users', {
          method: 'POST',
          body: JSON.stringify({ username, password, isAdmin }),
        });
        const newUser = await res.json();
        set((state) => ({ users: [...state.users, newUser] }));
      },

      updateUserAdminStatus: async (userId, isAdmin) => {
        const res = await apiCall(`/api/users/${userId}`, {
          method: 'PATCH',
          body: JSON.stringify({ isAdmin }),
        });
        const updated = await res.json();
        set((state) => ({
          users: state.users.map((u) => (u.id === userId ? updated : u)),
        }));
      },

      resetPassword: async (userId, newPassword) => {
        await apiCall(`/api/users/${userId}`, {
          method: 'PATCH',
          body: JSON.stringify({ password: newPassword }),
        });
      },

      userPermissions: (projectId) => {
        const state = get();
        const project = state.projects.find((candidate) => candidate.id === projectId);
        const isOwner = Boolean(project && state.currentUser && project.ownerId === state.currentUser.id);
        if (isOwner) {
          return { canEditTasks: true, canEditSubtasks: true, canComment: true, isOwner: true };
        }
        const share = state.projectShares[projectId]?.find((entry) => entry.userId === state.currentUser?.id);
        return {
          canEditTasks: share?.permissions.canEditTasks === true,
          canEditSubtasks: share?.permissions.canEditSubtasks === true,
          canComment: share?.permissions.canComment === true,
          isOwner: false,
        };
      },

      fetchShares: async (projectId) => {
        const res = await apiCall(`/api/projects/${projectId}/shares`);
        const shares = await res.json();
        set((state) => ({
          projectShares: { ...state.projectShares, [projectId]: Array.isArray(shares) ? shares : [] },
        }));
      },

      shareProject: async (projectId, userId, permissions) => {
        const res = await apiCall(`/api/projects/${projectId}/shares`, {
          method: 'POST',
          body: JSON.stringify({ userId, permissions }),
        });
        const share = await res.json();
        set((state) => {
          const current = state.projectShares[projectId] || [];
          const next = [...current.filter((entry) => entry.userId !== userId), share];
          return { projectShares: { ...state.projectShares, [projectId]: next } };
        });
      },

      unshareProject: async (projectId, userId) => {
        await apiCall(`/api/projects/${projectId}/shares/${userId}`, { method: 'DELETE' });
        set((state) => ({
          projectShares: {
            ...state.projectShares,
            [projectId]: (state.projectShares[projectId] || []).filter((entry) => entry.userId !== userId),
          },
        }));
      },

      searchUsers: async (query) => {
        if (!query.trim()) return [];
        const res = await apiCall(`/api/users/search?q=${encodeURIComponent(query.trim())}`);
        const users = await res.json();
        return Array.isArray(users) ? users : [];
      },

      fetchActivity: async (projectId) => {
        const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
        const res = await apiCall(`/api/activity${query}`);
        const entries = await res.json();
        return Array.isArray(entries) ? entries : [];
      },

      moveTask: (taskId, targetColumnId, newIndex) => {
        set((state) => {
          const updatedTasks = [...state.tasks];
          const taskIndex = updatedTasks.findIndex((t) => t.id === taskId);
          if (taskIndex === -1) return state;

          const [movedTask] = updatedTasks.splice(taskIndex, 1);
          movedTask.columnId = targetColumnId;

          const columnTasks = updatedTasks.filter((t) => t.columnId === targetColumnId);
          const otherTasks = updatedTasks.filter((t) => t.columnId !== targetColumnId);

          columnTasks.splice(newIndex, 0, movedTask);

          return { tasks: [...otherTasks, ...columnTasks] };
        });

        if (get().tasks.some((task) => task.id === taskId)) {
          const boardId = get().columns.find((c) => c.id === targetColumnId)?.projectId ?? '';
          syncApiCall(
            `/api/tasks/${taskId}`,
            { method: 'PUT', body: JSON.stringify({ column_id: targetColumnId }) },
            `move task ${taskId}`,
            boardId,
          );
        }
      },

      moveColumn: (columnId, newIndex) => {
        set((state) => {
          const columns = [...state.columns];
          const activeProjectId = state.activeProjectId;
          
          const projectColumns = columns.filter((c) => c.projectId === activeProjectId);
          const otherColumns = columns.filter((c) => c.projectId !== activeProjectId);

          const oldIndex = projectColumns.findIndex((c) => c.id === columnId);
          if (oldIndex === -1) return state;

          const [movedCol] = projectColumns.splice(oldIndex, 1);
          projectColumns.splice(newIndex, 0, movedCol);

          return { columns: [...otherColumns, ...projectColumns] };
        });
      },

      toggleSubtask: (taskId, subtaskId) => {
        set((state) => ({
          tasks: state.tasks.map((task) => {
            if (task.id !== taskId) return task;
            return {
              ...task,
              subtasks: task.subtasks.map((st) =>
                st.id === subtaskId ? { ...st, completed: !st.completed } : st
              ),
            };
          }),
        }));

        const updatedTask = get().tasks.find((task) => task.id === taskId);
        if (updatedTask) {
          const boardId = get().columns.find((c) => c.id === updatedTask.columnId)?.projectId ?? '';
          syncApiCall(
            `/api/tasks/${taskId}`,
            { method: 'PUT', body: JSON.stringify({ subtasks: updatedTask.subtasks }) },
            `toggle subtask on task ${taskId}`,
            boardId,
          );
        }
      },

      addProject: (newProj) => {
        const id = crypto.randomUUID();
        const newProject = { ...newProj, id, ownerId: get().currentUser?.id };
        set((state) => ({
          projects: [...state.projects, newProject],
          activeProjectId: id,
        }));
        syncApiCall(
          '/api/projects',
          {
            method: 'POST',
            body: JSON.stringify({
              id: newProject.id,
              name: newProject.name,
              color: newProject.color,
              category: newProject.category,
              archived: newProject.archived ?? false,
              tags: newProject.tags,
            }),
          },
          `add project ${id}`,
          id,
        );
      },

      updateProject: (id, updatedFields) => {
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === id ? { ...p, ...updatedFields } : p
          ),
        }));
        syncApiCall(
          `/api/projects/${id}`,
          { method: 'PUT', body: JSON.stringify(projectApiFields(updatedFields)) },
          `update project ${id}`,
          id,
        );
      },

      deleteProject: (id) => {
        set((state) => {
          const remainingProjects = state.projects.filter((p) => p.id !== id);
          const remainingColumns = state.columns.filter((c) => c.projectId !== id);
          const deletedColumnIds = new Set(
            state.columns.filter((c) => c.projectId === id).map((c) => c.id)
          );
          const remainingTasks = state.tasks.filter(
            (t) => !deletedColumnIds.has(t.columnId)
          );
          const nextActiveId =
            state.activeProjectId === id
              ? remainingProjects[0]?.id || ''
              : state.activeProjectId;

          return {
            projects: remainingProjects,
            columns: remainingColumns,
            tasks: remainingTasks,
            activeProjectId: nextActiveId,
          };
        });
        syncApiCall(
          `/api/projects/${id}`,
          { method: 'DELETE' },
          `delete project ${id}`,
          id,
        );
      },

      addColumn: (newCol) => {
        const id = crypto.randomUUID();
        const newColumn = { ...newCol, id };
        set((state) => ({
          columns: [...state.columns, newColumn],
        }));
        syncApiCall(
          '/api/columns',
          {
            method: 'POST',
            body: JSON.stringify({
              id: newColumn.id,
              project_id: newColumn.projectId,
              title: newColumn.title,
              color: newColumn.color,
              wip_limit: newColumn.wipLimit ?? 0,
            }),
          },
          `add column ${id}`,
          newColumn.projectId,
        );
      },

      updateColumn: (id, updatedFields) => {
        set((state) => ({
          columns: state.columns.map((c) =>
            c.id === id ? { ...c, ...updatedFields } : c
          ),
        }));
        const updateColBoardId = get().columns.find((c) => c.id === id)?.projectId ?? '';
        syncApiCall(
          `/api/columns/${id}`,
          { method: 'PUT', body: JSON.stringify(columnApiFields(updatedFields)) },
          `update column ${id}`,
          updateColBoardId,
        );
      },

      deleteColumn: (id) => {
        const boardId = get().columns.find((c) => c.id === id)?.projectId ?? '';
        set((state) => ({
          columns: state.columns.filter((c) => c.id !== id),
          tasks: state.tasks.filter((t) => t.columnId !== id),
        }));
        syncApiCall(
          `/api/columns/${id}`,
          { method: 'DELETE' },
          `delete column ${id}`,
          boardId,
        );
      },

      addTask: (newTask) => {
        const id = crypto.randomUUID();
        const newTaskWithId = { ...newTask, id };
        const addTaskBoardId = get().columns.find((c) => c.id === newTask.columnId)?.projectId ?? '';
        set((state) => ({
          tasks: [...state.tasks, newTaskWithId],
        }));
        syncApiCall(
          '/api/tasks',
          {
            method: 'POST',
            body: JSON.stringify({
              id: newTaskWithId.id,
              column_id: newTaskWithId.columnId,
              title: newTaskWithId.title,
              notes: newTaskWithId.notes,
              tags: newTaskWithId.tags,
              assignee: newTaskWithId.assignee,
              subtasks: newTaskWithId.subtasks,
              cost: newTaskWithId.cost,
              time: newTaskWithId.time,
              comments: newTaskWithId.comments,
              attachments: newTaskWithId.attachments,
              due_date: newTaskWithId.dueDate,
              priority: newTaskWithId.priority,
            }),
          },
          `add task ${id}`,
          addTaskBoardId,
        );
      },

      updateTask: (taskId, updatedFields) => {
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === taskId ? { ...t, ...updatedFields } : t
          ),
        }));
        const updatedTaskRef = get().tasks.find((t) => t.id === taskId);
        const updateTaskBoardId = get().columns.find((c) => c.id === updatedTaskRef?.columnId)?.projectId ?? '';
        syncApiCall(
          `/api/tasks/${taskId}`,
          { method: 'PUT', body: JSON.stringify(taskApiFields(updatedFields)) },
          `update task ${taskId}`,
          updateTaskBoardId,
        );
      },

      deleteTask: (taskId) => {
        const taskToDelete = get().tasks.find((task) => task.id === taskId);
        const deleteBoardId = get().columns.find((c) => c.id === taskToDelete?.columnId)?.projectId ?? '';
        set((state) => {
          if (!taskToDelete) return state;

          return {
            tasks: state.tasks.filter((t) => t.id !== taskId),
            trashedTasks: [taskToDelete, ...state.trashedTasks],
            lastDeletedTask: taskToDelete,
          };
        });
        if (taskToDelete) {
          syncApiCall(
            `/api/tasks/${taskId}`,
            { method: 'DELETE' },
            `delete task ${taskId}`,
            deleteBoardId,
          );
        }
      },

      restoreTask: (taskId) => {
        const targetId = taskId || get().lastDeletedTask?.id;
        const restoredTask = targetId
          ? get().trashedTasks.find((task) => task.id === targetId)
          : undefined;

        set((state) => {
          if (!targetId || !restoredTask) return state;

          return {
            tasks: [...state.tasks, restoredTask],
            trashedTasks: state.trashedTasks.filter((t) => t.id !== targetId),
            lastDeletedTask:
              state.lastDeletedTask?.id === targetId ? null : state.lastDeletedTask,
          };
        });
        if (restoredTask) {
          syncApiCall(
            '/api/tasks',
            {
              method: 'POST',
              body: JSON.stringify({
                id: restoredTask.id,
                column_id: restoredTask.columnId,
                title: restoredTask.title,
                notes: restoredTask.notes,
                tags: restoredTask.tags,
                assignee: restoredTask.assignee,
                subtasks: restoredTask.subtasks,
                cost: restoredTask.cost,
                time: restoredTask.time,
                comments: restoredTask.comments,
                attachments: restoredTask.attachments,
                due_date: restoredTask.dueDate,
                priority: restoredTask.priority,
              }),
            },
            `restore task ${restoredTask.id}`,
          );
        }
      },

      clearLastDeletedTask: () => set({ lastDeletedTask: null }),
      purgeTrash: () => set({ trashedTasks: [] }),

      // --- COMMENTS ---
      addComment: (taskId, text, author) => {
        set((state) => ({
          tasks: state.tasks.map((t) => {
            if (t.id !== taskId) return t;
            const newComment: Note & { author?: string } = {
              id: crypto.randomUUID(),
              text,
              author: author || state.currentUser?.username || 'Current User',
              createdAt: new Date().toISOString(),
            };
            return { ...t, comments: [...(t.comments || []), newComment] };
          }),
        }));
        const updatedTask = get().tasks.find((task) => task.id === taskId);
        if (updatedTask) {
          const boardId = get().columns.find((c) => c.id === updatedTask.columnId)?.projectId ?? '';
          syncApiCall(
            `/api/tasks/${taskId}`,
            { method: 'PUT', body: JSON.stringify({ comments: updatedTask.comments || [] }) },
            `add comment to task ${taskId}`,
            boardId,
          );
        }
      },

      deleteComment: (taskId, commentId) => {
        set((state) => ({
          tasks: state.tasks.map((t) => {
            if (t.id !== taskId) return t;
            return {
              ...t,
              comments: (t.comments || []).filter((c) => c.id !== commentId),
            };
          }),
        }));
        const updatedTask = get().tasks.find((task) => task.id === taskId);
        if (updatedTask) {
          const boardId = get().columns.find((c) => c.id === updatedTask.columnId)?.projectId ?? '';
          syncApiCall(
            `/api/tasks/${taskId}`,
            { method: 'PUT', body: JSON.stringify({ comments: updatedTask.comments || [] }) },
            `delete comment from task ${taskId}`,
            boardId,
          );
        }
      },

      // --- ATTACHMENTS ---
      addAttachment: (taskId, attachmentData) => {
        set((state) => ({
          tasks: state.tasks.map((t) => {
            if (t.id !== taskId) return t;
            const newAttachment: Attachment = {
              ...attachmentData,
              id: crypto.randomUUID(),
              createdAt: new Date().toISOString(),
            };
            return { ...t, attachments: [...(t.attachments || []), newAttachment] };
          }),
        }));
        const updatedTask = get().tasks.find((task) => task.id === taskId);
        if (updatedTask) {
          const boardId = get().columns.find((c) => c.id === updatedTask.columnId)?.projectId ?? '';
          syncApiCall(
            `/api/tasks/${taskId}`,
            { method: 'PUT', body: JSON.stringify({ attachments: updatedTask.attachments || [] }) },
            `add attachment to task ${taskId}`,
            boardId,
          );
        }
      },

      deleteAttachment: (taskId, attachmentId) => {
        const task = get().tasks.find((candidate) => candidate.id === taskId);
        const attachment = task?.attachments?.find((candidate) => candidate.id === attachmentId);
        const boardId = get().columns.find((c) => c.id === task?.columnId)?.projectId ?? '';
        set((state) => ({
          tasks: state.tasks.map((t) => {
            if (t.id !== taskId) return t;
            return {
              ...t,
              attachments: (t.attachments || []).filter((a) => a.id !== attachmentId),
            };
          }),
        }));
        const updatedTask = get().tasks.find((candidate) => candidate.id === taskId);
        if (updatedTask) {
          void (async () => {
            if (!navigator.onLine) {
              if (attachment?.fileUrl?.startsWith('offline://')) {
                try {
                  await offlineDb.fileCache.delete(attachment.fileUrl);
                } catch (err) {
                  console.warn('Failed to clear cached file:', err);
                }
              }
              if (boardId) {
                try {
                  await offlineDb.syncQueue.add({
                    operation: 'update',
                    entityType: 'task',
                    entityId: taskId,
                    boardId,
                    data: { attachments: updatedTask.attachments || [] } as Record<string, unknown>,
                    baseVersion: null,
                    queuedAt: new Date().toISOString(),
                    status: 'pending',
                    retries: 0,
                  });
                } catch (qErr) {
                  console.warn('Failed to queue attachment delete:', qErr);
                }
              }
              return;
            }
            if (attachment) {
              const filename = attachment.fileUrl.replace(/^\/uploads\//, '').split('?')[0];
              try {
                await apiCall(`/api/files/${filename}`, { method: 'DELETE' });
              } catch (err) {
                console.warn(`Failed to delete attachment file for task ${taskId}:`, err);
              }
            }
            try {
              await apiCall(`/api/tasks/${taskId}`, {
                method: 'PUT',
                body: JSON.stringify({ attachments: updatedTask.attachments || [] }),
              });
            } catch (err) {
              console.warn(`Failed to sync deleted attachment for task ${taskId}:`, err);
            }
          })();
        }
      },

      // --- SUBTASK NOTES ---
      addSubtaskNote: (taskId, subtaskId, text, author) => {
        set((state) => ({
          tasks: state.tasks.map((t) => {
            if (t.id !== taskId) return t;
            return {
              ...t,
              subtasks: t.subtasks.map((st) => {
                if (st.id !== subtaskId) return st;
                const newNote: Note & { author?: string } = {
                  id: crypto.randomUUID(),
                  text,
                  author: author || state.currentUser?.username || 'Current User',
                  createdAt: new Date().toISOString(),
                };
                return { ...st, notes: [...(st.notes || []), newNote] };
              }),
            };
          }),
        }));
        const updatedTask = get().tasks.find((task) => task.id === taskId);
        if (updatedTask) {
          const boardId = get().columns.find((c) => c.id === updatedTask.columnId)?.projectId ?? '';
          syncApiCall(
            `/api/tasks/${taskId}`,
            { method: 'PUT', body: JSON.stringify({ subtasks: updatedTask.subtasks }) },
            `add subtask note to task ${taskId}`,
            boardId,
          );
        }
      },

      deleteSubtaskNote: (taskId, subtaskId, noteId) => {
        set((state) => ({
          tasks: state.tasks.map((t) => {
            if (t.id !== taskId) return t;
            return {
              ...t,
              subtasks: t.subtasks.map((st) => {
                if (st.id !== subtaskId) return st;
                return {
                  ...st,
                  notes: (st.notes || []).filter((n) => n.id !== noteId),
                };
              }),
            };
          }),
        }));
        const updatedTask = get().tasks.find((task) => task.id === taskId);
        if (updatedTask) {
          const boardId = get().columns.find((c) => c.id === updatedTask.columnId)?.projectId ?? '';
          syncApiCall(
            `/api/tasks/${taskId}`,
            { method: 'PUT', body: JSON.stringify({ subtasks: updatedTask.subtasks }) },
            `delete subtask note from task ${taskId}`,
            boardId,
          );
        }
      },

      importStoreData: (data) => {
        const projects = data.projects || [];
        const columns = data.columns || [];
        const tasks = data.tasks || [];
        set({
          projects,
          columns,
          tasks,
          activeProjectId: projects[0]?.id || '',
        });

        projects.forEach((project) => {
          syncApiCall(
            '/api/projects',
            { method: 'POST', body: JSON.stringify(projectApiFields(project)) },
            `import project ${project.id}`,
          );
        });
        columns.forEach((column) => {
          syncApiCall(
            '/api/columns',
            {
              method: 'POST',
              body: JSON.stringify({
                id: column.id,
                project_id: column.projectId,
                title: column.title,
                color: column.color,
                wip_limit: column.wipLimit ?? 0,
              }),
            },
            `import column ${column.id}`,
          );
        });
        tasks.forEach((task) => {
          syncApiCall(
            '/api/tasks',
            { method: 'POST', body: JSON.stringify({ id: task.id, ...taskApiFields(task) }) },
            `import task ${task.id}`,
          );
        });
      },

      getActiveProjectColumns: () => {
        const { columns, activeProjectId } = get();
        return columns.filter((c) => c.projectId === activeProjectId);
      },

      getColumnTasks: (columnId) => {
        const state = get();
        let tasks = state.tasks.filter((t) => t.columnId === columnId);
        
        if (state.filterHasAttachments) {
          tasks = tasks.filter((t) => t.attachments && t.attachments.length > 0);
        }

        if (state.searchQuery.trim()) {
          const q = state.searchQuery.toLowerCase();
          tasks = tasks.filter((t) => 
            t.title.toLowerCase().includes(q) || 
            (t.notes && t.notes.toLowerCase().includes(q)) ||
            (t.assignee?.name && t.assignee.name.toLowerCase().includes(q)) ||
            (t.tags && t.tags.some(tag => {
              const label = typeof tag === 'string' ? tag : tag?.label || '';
              return label.toLowerCase().includes(q);
            }))
          );
        }
        
        return tasks;
      },
    }),
    { 
      name: 'kanban-storage',
      onRehydrateStorage: () => (state) => {
        if (state?.darkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    }
  )
);